import React from 'react'
import moment from 'moment'
import Card from 'react-bootstrap/Card'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClock } from '@fortawesome/free-solid-svg-icons'
import { accentForItem, initials, OWNER_AVATAR_BG } from './accent'
import { DocumentThumbnail, DatasetThumbnail, SampleThumbnail } from './thumbnails'
import { ITEM_TYPES } from './item-shape'

// One card for all three item types. The layout came from the Reports index; Views and Samples
// adopted it so a mixed grid reads as one system rather than two. What varies per type is the
// thumbnail and the meta counts — everything else is shared.
function Thumbnail({ item, accent }) {
  const { icon, label } = ITEM_TYPES[item.type]
  // Only the document is tinted; the other two draw themselves in the shared neutral, so they take
  // no accent at all rather than being handed one they would have to ignore.
  // A report overrides the type word to say which side of a split it is on.
  const shared = { typeIcon: icon, typeLabel: item.typeLabel || label }

  if (item.type === 'report') return <DocumentThumbnail {...shared} accent={accent} id={item.id} />
  if (item.type === 'sample') {
    return (
      <SampleThumbnail
        {...shared}
        completeCount={item.completeCount}
        totalCount={item.totalCount}
      />
    )
  }
  return <DatasetThumbnail {...shared} />
}

function ItemCard({ item, onOpen, actions }) {
  const accent = accentForItem(item.type, item.id)
  const isScheduled = (item.scheduledEmails?.length || 0) > 0

  return (
    <Card
      className="h-100 shadow-sm item-card"
      role="button"
      onClick={() => onOpen(item)}
      style={{ cursor: 'pointer' }}
    >
      <Thumbnail item={item} accent={accent} />

      <Card.Body className="d-flex flex-column">
        <div className="d-flex align-items-start justify-content-between mb-1">
          <Card.Title className="h6 mb-0 me-2 text-truncate" title={item.name}>
            {item.name}
          </Card.Title>
          <div
            className="d-flex align-items-center gap-1 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {isScheduled && (
              <FontAwesomeIcon icon={faClock} className="text-muted" title="Has a scheduled email" />
            )}
            {actions}
          </div>
        </div>

        {item.sectionLabel && (
          <p className="text-muted small mb-1 text-truncate" title={item.sectionLabel}>
            {item.sectionLabel}
          </p>
        )}

        {item.description && (
          <p className="text-muted small mb-3 text-truncate" title={item.description}>
            {item.description}
          </p>
        )}

        <div className="d-flex align-items-center gap-3 text-muted small mt-auto">
          {item.meta.map((entry, i) => (
            <span key={i} title={entry.title}>
              <FontAwesomeIcon icon={entry.icon} className="me-1" />
              {entry.label}
            </span>
          ))}
        </div>
      </Card.Body>

      <Card.Footer className="bg-white d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2 text-truncate">
          {/* Grey, not the item's accent: this circle marks *who owns* the item, and tinting it
              per report made the same person read as a different colour on every card. */}
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle text-white flex-shrink-0"
            style={{ width: 24, height: 24, fontSize: '0.65rem', background: OWNER_AVATAR_BG }}
            title={item.userFullName}
          >
            {initials(item.userFullName)}
          </span>
          <span className="text-muted small text-truncate">{item.userFullName}</span>
        </div>
        {item.updatedAt && (
          <span className="text-muted small flex-shrink-0">
            Edited {moment(item.updatedAt).fromNow()}
          </span>
        )}
      </Card.Footer>
    </Card>
  )
}

export default ItemCard
